import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { ListBase, Pagination, FilterContext, Empty, useListContext } from 'react-admin';
import { Card } from '@mui/material';
import { useLastColumnShadow } from '@/composables/useLastColumnShadow';
import { SyncedFilterForm } from '@/components/global/filters/SyncedFilterForm.jsx';
import { getStickyLastColumnSx } from '@/themes/commonStyles/stickyLastColumn.js';
import { styled } from '@mui/material/styles';

const defaultEmpty = <Empty />;

const StyledCard = styled(Card)(() => ({
    '&.scroll-container': {
        '& .RaDatagrid-table': {
            position: 'relative',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            overflow: 'hidden',
        },
        '& .MuiTableSortLabel-root': {
            fontWeight: 600,
            whiteSpace: 'nowrap',
        },
    },
}));

const StyledFilterForm = styled(SyncedFilterForm)(() => ({
    padding: '24px 0',
}));

const SimpleListContent = ({
    children,
    filters = [],
    empty = defaultEmpty,
    disablePagination = false,
    paginationClassName = '',
    addLastColumnShadow = false,
    className,
    filtersAlwaysOn = false,
}) => {
    const { data, isPending } = useListContext();
    const scrollContainerRef = useRef(null);
    const isTableReady = !isPending && !!data;
    const { showShadow } = useLastColumnShadow(scrollContainerRef, isTableReady);

    const shouldRenderEmptyPage = !isPending && data?.length === 0 && empty !== false;

    if (!children || (!data && isPending)) {
        return null;
    }

    return (
        <div className={className}>
            {filtersAlwaysOn && shouldRenderEmptyPage && (
                <FilterContext.Provider value={filters}>
                    <StyledFilterForm />
                </FilterContext.Provider>
            )}
            {shouldRenderEmptyPage ? (
                empty
            ) : (
                <>
                    {filters && filters.length > 0 && (
                        <FilterContext.Provider value={filters}>
                            <StyledFilterForm />
                        </FilterContext.Provider>
                    )}

                    <StyledCard className='scroll-container' ref={scrollContainerRef} sx={getStickyLastColumnSx(addLastColumnShadow, showShadow)}>
                        {children}
                    </StyledCard>
                    {!disablePagination && <Pagination className={paginationClassName} />}
                </>
            )}
        </div>
    );
};

SimpleListContent.propTypes = {
    children: PropTypes.node,
    filters: PropTypes.array,
    empty: PropTypes.node,
    disablePagination: PropTypes.bool,
    paginationClassName: PropTypes.string,
    addLastColumnShadow: PropTypes.bool,
    className: PropTypes.string,
    filtersAlwaysOn: PropTypes.bool,
};

const defaultFilter = {};

const SimpleList = ({
    debounce,
    disableAuthentication,
    filter = defaultFilter,
    filterDefaultValues,
    perPage = 10,
    queryOptions,
    resource,
    sort,
    storeKey,
    disablePagination = false,
    paginationClassName = '',
    addLastColumnShadow = false,
    className,
    ...rest
}) => (
    <ListBase
        debounce={debounce}
        disableAuthentication={disableAuthentication}
        disableSyncWithLocation={true}
        filter={filter}
        filterDefaultValues={filterDefaultValues}
        perPage={perPage}
        queryOptions={queryOptions}
        resource={resource}
        sort={sort}
        storeKey={storeKey}
    >
        <SimpleListContent
            disablePagination={disablePagination}
            paginationClassName={paginationClassName}
            addLastColumnShadow={addLastColumnShadow}
            className={className}
            {...rest}
        />
    </ListBase>
);

export default SimpleList;
